import { Inject, Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { UnespCoreEnvironment } from '@unesp/core'
import { identity, Observable } from 'rxjs'
import { ErrorHandlerService } from './error-handler.service'
import { Departamento } from '../models/departamento'
import { UnidadeUniversitaria } from '../models/unidade-universitaria'
import { DiscenteInscricao } from '../models/discente-inscricao'
import { StatusResponse } from '../models/status-response'
import { CargaHorariaCompleto } from '../models/carga-horaria-completo'
import { CargaHoraria } from '../models/carga-horaria'
import { DiscentePlanoAtividadeEntregar } from '../models/discente-plano-atividade-entregar'
import { DiscenteDisciplina } from '../models/discente-disciplina'
import { StatusInscricao } from '../models/status-inscricao'

@Injectable({
  providedIn: 'root',
})
export class DiscenteService {
  private baseUrl: string

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    @Inject('environment') env: UnespCoreEnvironment
  ) {
    this.baseUrl = env.baseUrlApi
  }

  listarUnidadeUniversitaria(ano: number): Observable<UnidadeUniversitaria[]> {
    return this.http
      .get<UnidadeUniversitaria[]>(`${this.baseUrl}/discente/unidade-universitaria/${ano}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  listarDepartamento(ano: number, idUnidadeUniversitaria: number): Observable<Departamento[]> {
    return this.http
      .get<Departamento[]>(`${this.baseUrl}/discente/departamento/${ano}/${idUnidadeUniversitaria}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  inscricao(idDepartamentoSecao: number, ano: number, cpf: string): Observable<DiscenteInscricao[]> {
    return this.http
      .get<DiscenteInscricao[]>(`${this.baseUrl}/discente/inscricao/${idDepartamentoSecao}/${ano}/${cpf}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  incluirInscricao(cpf: string, ano: number, semestre: number, data: DiscenteInscricao): Observable<StatusResponse> {
    return this.http
      .post<StatusResponse>(`${this.baseUrl}/discente/incluir-inscricao/${cpf}/${ano}/${semestre}`, data)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  removerInscricao(cpf: string, ano: number, semestre: number, idDisciplina: number): Observable<StatusResponse> {
    return this.http
      .delete<StatusResponse>(`${this.baseUrl}/discente/remover-inscricao/${cpf}/${ano}/${semestre}/${idDisciplina}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  cargaHoraria(cpf: string, ano: number): Observable<DiscenteDisciplina[]> {
    return this.http
      .get<DiscenteDisciplina[]>(`${this.baseUrl}/discente/carga-horaria/${cpf}/${ano}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  cargaHorariaPrevista(idInscricao: number): Observable<CargaHorariaCompleto> {
    return this.http
      .get<CargaHorariaCompleto>(`${this.baseUrl}/discente/carga-horaria/${idInscricao}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  cargaHorariaDocumento(id: number): Observable<any> {
    let headerOptions = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
    })
    let requestOptions = { headers: headerOptions, responseType: 'blob' as 'blob' }
    return this.http
      .get(`${this.baseUrl}/discente/carga-horaria/${id}/documento`, requestOptions)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  registrarCargaHorariaPrevista(idInscricao: number, data: CargaHoraria[]): Observable<StatusResponse> {
    return this.http
      .post<StatusResponse>(`${this.baseUrl}/discente/carga-horaria/${idInscricao}`, data)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  planoAtividade(cpf: string, ano: number): Observable<DiscenteDisciplina[]> {
    return this.http
      .get<DiscenteDisciplina[]>(`${this.baseUrl}/discente/entregar-plano-atividade/${cpf}/${ano}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  entregarPlanoAtividade(idInscricao: number): Observable<DiscentePlanoAtividadeEntregar> {
    return this.http
      .get<DiscentePlanoAtividadeEntregar>(`${this.baseUrl}/discente/entregar-plano-atividade/${idInscricao}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  registrarPlanoAtividade(idInscricao: number, formData: FormData): Observable<StatusResponse> {
    return this.http
      .post<StatusResponse>(`${this.baseUrl}/discente/entregar-plano-atividade/${idInscricao}`, formData)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  relatorioAtividade(cpf: string, ano: number): Observable<DiscenteDisciplina[]> {
    return this.http
      .get<DiscenteDisciplina[]>(`${this.baseUrl}/discente/entregar-relatorio-atividade/${cpf}/${ano}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  entregarRelatorioAtividade(idInscricao: number): Observable<DiscentePlanoAtividadeEntregar> {
    return this.http
      .get<DiscentePlanoAtividadeEntregar>(`${this.baseUrl}/discente/entregar-relatorio-atividade/${idInscricao}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  registrarRelatorioAtividade(idInscricao: number, formData: FormData): Observable<StatusResponse> {
    return this.http
      .post<StatusResponse>(`${this.baseUrl}/discente/entregar-relatorio-atividade/${idInscricao}`, formData)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  emissaoCertificado(cpf: string, ano: number): Observable<DiscenteDisciplina[]> {
    return this.http
      .get<DiscenteDisciplina[]>(`${this.baseUrl}/discente/emissao-certificado/${cpf}/${ano}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  certificado(id: number): Observable<any> {
    let headerOptions = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
    })
    let requestOptions = { headers: headerOptions, responseType: 'blob' as 'blob' }
    return this.http
      .get(`${this.baseUrl}/discente/emissao-certificado/${id}`, requestOptions)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  statusInscricao(cpf: string, ano: number): Observable<StatusInscricao[]> {
    return this.http
      .get<StatusInscricao[]>(`${this.baseUrl}/discente/status-inscricao/${cpf}/${ano}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  statusInscricoes(cpf: string): Observable<StatusInscricao[]> {
    return this.http
      .get<StatusInscricao[]>(`${this.baseUrl}/discente/status-inscricao/${cpf}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  download(folder: string, idInscricao: number): Observable<any> {
    let headerOptions = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
    })
    let requestOptions = { headers: headerOptions, responseType: 'blob' as 'blob' }
    return this.http
      .get(`${this.baseUrl}/discente/download/${folder}/${idInscricao}`, requestOptions)
      .pipe(identity, this.errorHandlerService.catchError())
  }
}
